'use client';

import { useEffect, useRef, useState } from 'react';
import { createWorker, OEM, PSM } from 'tesseract.js';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type PdfJsViewport = {
  width: number;
  height: number;
};

type PdfJsPage = {
  getViewport: (params: { scale: number }) => PdfJsViewport;
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: PdfJsViewport;
  }) => { promise: Promise<void> };
  cleanup?: () => void;
};

type PdfJsDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfJsPage>;
  cleanup?: () => void;
  destroy?: () => Promise<void> | void;
};

type PdfJsModule = {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  getDocument: (params: {
    data: Uint8Array;
    disableWorker: boolean;
    useSystemFonts?: boolean;
    isEvalSupported?: boolean;
  }) => {
    promise: Promise<PdfJsDocument>;
  };
};

type OcrPdfSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type OcrProgress = {
  label: string;
  progress: number;
};

type TesseractWorkerLike = Awaited<ReturnType<typeof createWorker>>;

const maxRenderEdge = 2200;
const maxCanvasPixels = 6_500_000;

let pdfJsModulePromise: Promise<PdfJsModule> | null = null;

function loadPdfJsModule() {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise =
      import('pdfjs-dist/legacy/build/pdf.mjs') as unknown as Promise<PdfJsModule>;
  }

  return pdfJsModulePromise.then((module) => {
    if (!module.GlobalWorkerOptions.workerSrc) {
      module.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
    }

    return module;
  });
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createTextFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return `cleanor.app-ocr-pdf-to-text-${pageLabel}-${formatExportTimestamp(new Date())}.txt`;
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

function createTextBlob(text: string) {
  return new Blob([text], { type: 'text/plain;charset=utf-8' });
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function formatOcrStatus(status: string) {
  switch (status) {
    case 'loading tesseract core':
      return 'Loading OCR engine…';
    case 'initializing tesseract':
      return 'Initializing OCR engine…';
    case 'loading language traineddata':
      return 'Loading language data…';
    case 'initializing api':
      return 'Preparing OCR worker…';
    case 'recognizing text':
      return 'Recognizing current page…';
    default:
      return status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}…` : 'Processing…';
  }
}

function normalizeRecognizedText(text: string) {
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

function getRenderScale(viewport: PdfJsViewport, preferredScale: number) {
  const basePixels = viewport.width * viewport.height;
  const edgeScale = maxRenderEdge / Math.max(viewport.width, viewport.height);
  const pixelScale = Math.sqrt(maxCanvasPixels / basePixels);

  return Math.max(1, Math.min(preferredScale, edgeScale, pixelScale));
}

async function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('The PDF preview could not be created.'));
      },
      'image/jpeg',
      quality,
    );
  });
}

async function createPreviewUrl(pdfDocument: PdfJsDocument) {
  const page = await pdfDocument.getPage(1);

  try {
    const baseViewport = page.getViewport({ scale: 1 });
    const previewScale = Math.max(
      0.7,
      Math.min(1.05, 280 / Math.max(baseViewport.width, baseViewport.height)),
    );
    const viewport = page.getViewport({ scale: previewScale });
    const canvas = window.document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));

    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, canvas, viewport }).promise;
    const blob = await canvasToJpegBlob(canvas, 0.82);
    canvas.width = 0;
    canvas.height = 0;
    return URL.createObjectURL(blob);
  } finally {
    page.cleanup?.();
  }
}

async function readPdfSource(file: File): Promise<OcrPdfSource> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdfjs = await loadPdfJsModule();
  const loadingTask = pdfjs.getDocument({
    data: bytes,
    disableWorker: true,
    useSystemFonts: true,
    isEvalSupported: false,
  });

  let pdfDocument: PdfJsDocument | null = null;

  try {
    pdfDocument = await loadingTask.promise;

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: pdfDocument.numPages,
      previewUrl: await createPreviewUrl(pdfDocument),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    pdfDocument?.cleanup?.();
    await pdfDocument?.destroy?.();
  }
}

function buildCombinedText(pageTexts: string[]) {
  if (pageTexts.length <= 1) {
    return pageTexts[0] ?? '';
  }

  return pageTexts
    .map(
      (pageText, index) =>
        `Page ${index + 1}\n${'-'.repeat(8)}\n${pageText || '[No text recognized]'}`,
    )
    .join('\n\n');
}

export default function BrowserOcrPdfToText() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const workerRef = useRef<TesseractWorkerLike | null>(null);
  const activePageRef = useRef(0);
  const totalPagesRef = useRef(0);
  const [source, setSource] = useState<OcrPdfSource | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [progress, setProgress] = useState<OcrProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [recognizedText, setRecognizedText] = useState('');
  const [averageConfidence, setAverageConfidence] = useState<number | null>(null);
  const [wordCount, setWordCount] = useState<number | null>(null);
  const [processedPages, setProcessedPages] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }

      void workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [source]);

  function replaceSource(nextSource: OcrPdfSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  function resetResultState() {
    setCreditMessage(null);
    setErrorMessage(null);
    setRecognizedText('');
    setAverageConfidence(null);
    setWordCount(null);
    setProcessedPages(null);
    setProgress(null);
  }

  async function handleFileSelection(file: File | null) {
    resetResultState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to extract text from.');
      return;
    }

    try {
      const nextSource = await readPdfSource(file);
      replaceSource(nextSource);
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    resetResultState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function copyText() {
    if (!recognizedText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(recognizedText);
      setCreditMessage('Text copied to clipboard.');
      setErrorMessage(null);
    } catch {
      setErrorMessage('Clipboard access is blocked in this browser.');
    }
  }

  function downloadText() {
    if (!recognizedText || !source) {
      return;
    }

    const blob = createTextBlob(recognizedText);
    const nextUrl = URL.createObjectURL(blob);
    triggerBrowserDownload(nextUrl, createTextFileName(source.pageCount));
    URL.revokeObjectURL(nextUrl);
  }

  async function runOcr() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    setIsRecognizing(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({ label: 'Checking credits and preparing OCR…', progress: 0 });
    activePageRef.current = 0;
    totalPagesRef.current = source.pageCount;

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'ocr-pdf-to-text' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another OCR run right now.';
        throw new Error(message);
      }

      const worker = await createWorker('eng', OEM.LSTM_ONLY, {
        workerPath: '/tesseract-worker.min.js',
        corePath: '/tesseract-core-simd-lstm.wasm.js',
        logger: (message: { status: string; progress: number }) => {
          const totalPages = Math.max(1, totalPagesRef.current);
          const currentPage = Math.max(1, activePageRef.current || 1);
          const currentProgress = Math.max(0, Math.min(1, message.progress ?? 0));
          const overallProgress = Math.max(
            0,
            Math.min(1, (currentPage - 1 + currentProgress) / totalPages),
          );

          setProgress({
            label:
              message.status === 'recognizing text'
                ? `Recognizing page ${currentPage} of ${totalPages}…`
                : formatOcrStatus(message.status),
            progress: overallProgress,
          });
        },
      });
      workerRef.current = worker;

      await worker.setParameters({
        tessedit_pageseg_mode: PSM.AUTO,
        preserve_interword_spaces: '1',
      });

      const pdfjs = await loadPdfJsModule();
      const bytes = new Uint8Array(await source.file.arrayBuffer());
      const loadingTask = pdfjs.getDocument({
        data: bytes,
        disableWorker: true,
        useSystemFonts: true,
        isEvalSupported: false,
      });

      let pdfDocument: PdfJsDocument | null = null;

      try {
        pdfDocument = await loadingTask.promise;
        const pageTexts: string[] = [];
        const pageConfidences: number[] = [];

        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
          activePageRef.current = pageNumber;
          setProcessedPages(pageNumber - 1);
          setProgress({
            label: `Preparing page ${pageNumber} of ${pdfDocument.numPages}…`,
            progress: (pageNumber - 1) / pdfDocument.numPages,
          });

          const page = await pdfDocument.getPage(pageNumber);

          try {
            const baseViewport = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({ scale: getRenderScale(baseViewport, 1.85) });
            const canvas = window.document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(viewport.width));
            canvas.height = Math.max(1, Math.round(viewport.height));

            const context = canvas.getContext('2d');

            if (!context) {
              throw new Error('Your browser could not render this PDF page for OCR.');
            }

            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            await page.render({ canvasContext: context, canvas, viewport }).promise;
            const result = await worker.recognize(canvas);
            const nextText = normalizeRecognizedText(result.data.text ?? '');

            pageTexts.push(nextText);

            if (typeof result.data.confidence === 'number') {
              pageConfidences.push(result.data.confidence);
            }

            canvas.width = 0;
            canvas.height = 0;
            setProcessedPages(pageNumber);
          } finally {
            page.cleanup?.();
          }
        }

        const combinedText = buildCombinedText(pageTexts);
        const nextWordCount = combinedText ? combinedText.split(/\s+/).filter(Boolean).length : 0;
        const nextAverageConfidence =
          pageConfidences.length > 0
            ? Math.round(
                pageConfidences.reduce((sum, value) => sum + value, 0) / pageConfidences.length,
              )
            : null;

        setRecognizedText(combinedText);
        setAverageConfidence(nextAverageConfidence);
        setWordCount(nextWordCount);
        setCreditMessage(
          usagePayload.data?.credits?.plan === 'pro'
            ? 'PDF OCR finished. Unlimited plan active.'
            : usagePayload.data?.credits?.remaining !== undefined
              ? `PDF OCR finished. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
              : 'PDF OCR finished.',
        );
      } finally {
        pdfDocument?.cleanup?.();
        await pdfDocument?.destroy?.();
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Text could not be extracted from this PDF.',
      );
    } finally {
      setProgress(null);
      setIsRecognizing(false);
      activePageRef.current = 0;
      totalPagesRef.current = 0;
      await workerRef.current?.terminate();
      workerRef.current = null;
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Extract text from one PDF in the browser</h2>
          <p>
            Upload one scanned or image-heavy PDF, run OCR page by page locally, and copy or
            download the extracted text without sending the file to your server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? 'PDF ready for OCR' : 'Choose one PDF or drop it here'}</strong>
              <span>
                {source
                  ? `${source.pageCount} pages • ${formatFileSize(source.size)}`
                  : 'Built for scanned PDFs, exported scans, receipts, and image-based documents'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isRecognizing}
              onClick={() => void runOcr()}
              type="button"
            >
              {isRecognizing ? 'Extracting text…' : 'Extract text'}
            </button>
            {recognizedText ? (
              <button className="button button-ghost" onClick={() => void copyText()} type="button">
                Copy text
              </button>
            ) : null}
            {recognizedText ? (
              <button className="button button-ghost" onClick={downloadText} type="button">
                Download TXT
              </button>
            ) : null}
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-live-panel-note tool-pdf-progress" aria-live="polite">
              <strong>{progress.label}</strong>
              <progress max={100} value={Math.round(progress.progress * 100)} />
              <span>{`${Math.round(progress.progress * 100)}%`}</span>
            </div>
          ) : null}

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side PDF OCR extractor</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → Text</span>
            <span>OCR</span>
            <span>Copy ready</span>
            <span>Local processing</span>
          </div>
          <p>
            This is built for one fast PDF OCR job at a time: pick the file, extract page text in
            the browser, and move on with copyable or downloadable plain text.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Scanned or image-based PDFs work best here. If your PDF already contains selectable
              text, a direct text extractor would be faster than OCR.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source PDF</strong>
                <span>{source.pageCount} pages</span>
              </div>
              <div>
                <strong>File size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              {processedPages !== null ? (
                <div>
                  <strong>Pages processed</strong>
                  <span>{`${processedPages} / ${source.pageCount}`}</span>
                </div>
              ) : null}
              {averageConfidence !== null ? (
                <div>
                  <strong>Average confidence</strong>
                  <span>{`${averageConfidence}%`}</span>
                </div>
              ) : null}
              {wordCount !== null ? (
                <div>
                  <strong>Recognized words</strong>
                  <span>{wordCount}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>First page preview</h3>
          <div className="tool-live-preview-frame">
            <img alt={`${source.name} first page`} src={source.previewUrl} />
          </div>
        </div>
      ) : null}

      {source ? (
        <div className="tool-live-preview">
          <h3>Recognized text</h3>
          <div className="tool-live-panel tool-ocr-output-shell">
            <textarea
              className="tool-ocr-output"
              placeholder="Run OCR to extract text from the current PDF."
              readOnly
              value={recognizedText}
            />
          </div>
        </div>
      ) : null}
    </article>
  );
}
